import React from 'react';
import { Loader } from 'components/Loader';
import ReactCrop, { Crop } from 'react-image-crop';
import useStyles from './ImageWithDifferentRatios.styles';
import { Button, DialogActions, DialogContent, Dialog, Typography } from '@material-ui/core';
import { useImageWithDifferentRatios } from './hooks/useImageWithDifferentRatios';

interface Props {
  open: boolean;
  url: string | null;
  handleClose: (url: string) => void;
}

export const ImageWithDifferentRatios: React.FC<Props> = ({ open, url, handleClose }: Props) => {
  const classes = useStyles();
  const {
    imgRef,
    crop,
    imgUrl,
    loading,
    canvasRef,
    isDisplayCampaign,
    handleCrop,
    validateCrop,
    handleConfirm,
    handleCompletedCrop
  } = useImageWithDifferentRatios(url);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth={'md'}
      onClose={handleClose}
      disableBackdropClick
      classes={{ paper: classes.paper }}
    >
      <DialogContent className={classes.content}>
        <div className={classes.ratios}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => handleCrop({ unit: '%', x: 0, y: 0, width: 0, height: 100, aspect: 1 })}
          >
            1X1
          </Button>

          {!isDisplayCampaign && (
            <Button
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => handleCrop({ unit: '%', x: 0, y: 0, width: 0, height: 100, aspect: 4 / 5 })}
            >
              4X5
            </Button>
          )}

          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => handleCrop({ unit: '%', x: 0, y: 0, width: 0, height: 100, aspect: 1.91 })}
          >
            1.91X1
          </Button>
        </div>

        <div className={classes.crop}>
          {loading ? (
            <Loader />
          ) : (
            imgUrl && (
              <div className={classes.cropContainer}>
                <ReactCrop
                  src={imgUrl}
                  ruleOfThirds
                  crop={crop as Crop}
                  onChange={(c) => handleCrop(c)}
                  onComplete={(c) => handleCompletedCrop(c)}
                  onImageLoaded={(img) => {
                    imgRef.current = img;
                  }}
                  imageStyle={{ display: 'block', maxWidth: '100%', maxHeight: '400px', width: 'auto', height: 'auto' }}
                />

                {/* In order to get the relational dimension from original image  */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                <div>
                  <Typography color="secondary" variant="caption" display="block">
                    <strong>
                      Cropped Image Size: {Math.round(canvasRef.current?.width || 0)}x
                      {Math.round(canvasRef.current?.height || 0)}
                    </strong>
                  </Typography>

                  <Typography color="secondary" variant="caption" display="block">
                    <strong>
                      Original Image Size: {Math.round(imgRef.current?.naturalWidth || 0)}x
                      {Math.round(imgRef.current?.naturalHeight || 0)}
                    </strong>
                  </Typography>

                  <Typography variant="caption" display="block" className={classes.error}>
                    {!validateCrop.valid && validateCrop.message}
                  </Typography>
                </div>
              </div>
            )
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button disabled={loading} color="secondary" size="small" variant="text" onClick={() => handleClose(url!)}>
          Cancel
        </Button>

        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={loading || !validateCrop.valid}
          onClick={async () => {
            await handleConfirm();
            handleClose(url!);
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
